import { Bandeau, Bouton, Champ, Selection } from '@cc/ui';
import { useState } from 'react';
import { useAction } from '../../commun/useAction';
import { INVALIDER_MARCHES } from './api';
import { creerRangee, type Allee } from './geographie-api';
import styles from './marches.module.css';
import { useRubriques } from '../recettes/requetes';

export function EditeurRangee({ marcheId, allees }: { marcheId: string; allees: Allee[] }) {
  const [allee, setAllee] = useState('');
  const [nombre, setNombre] = useState(20);
  const [pas, setPas] = useState(2);
  const [cote, setCote] = useState<'gauche' | 'droite' | 'deux'>('deux');
  const [prefixe, setPrefixe] = useState('');
  const [premier, setPremier] = useState(1);
  const [surface, setSurface] = useState(4);
  const [rubrique, setRubrique] = useState('');
  const rubriques = useRubriques();
  const action = useAction({
    executer: () =>
      creerRangee(marcheId, allee, {
        nombre,
        pas,
        cote,
        prefixe,
        premier_numero: premier,
        surface_m2: surface,
        rubrique_code: rubrique,
      }),
    succes: (r) => `${r.elements.length} places créées.`,
    invalider: INVALIDER_MARCHES,
  });
  return (
    <form
      className={styles.pile}
      onSubmit={(e) => {
        e.preventDefault();
        action.lancer(undefined);
      }}
      aria-label="Créer une rangée de places"
    >
      <Selection
        libelle="Allée"
        value={allee}
        onChange={(e) => {
          setAllee(e.target.value);
          setPrefixe(allees.find((a) => a.id === e.target.value)?.code ?? '');
        }}
        required
        optionVide="Choisir une allée"
        options={allees.map((a) => ({ valeur: a.id, libelle: `${a.code} · ${a.nom}` }))}
      />
      <Selection
        libelle="Redevance au forfait mensuel"
        value={rubrique}
        required
        optionVide="Choisir la rubrique"
        onChange={(e) => setRubrique(e.target.value)}
        options={(rubriques.data ?? [])
          .filter((r) => r.active && r.base_calcul === 'forfait' && r.periodicite === 'mensuelle')
          .map((r) => ({ valeur: r.code, libelle: r.libelle }))}
      />
      {rubriques.error ? (
        <Bandeau type="erreur">Le catalogue des redevances n’a pas pu être chargé.</Bandeau>
      ) : null}
      <div className={styles.barreFiltres}>
        <Champ
          libelle="Nombre de places"
          type="number"
          min={1}
          max={1000}
          value={nombre}
          onChange={(e) => setNombre(Number(e.target.value))}
          required
        />
        <Champ
          libelle="Pas entre rangs (m)"
          type="number"
          min={0.5}
          max={100}
          step={0.5}
          value={pas}
          onChange={(e) => setPas(Number(e.target.value))}
          required
        />
        <Selection
          libelle="Côté de l’allée"
          value={cote}
          onChange={(e) => setCote(e.target.value as typeof cote)}
          options={[
            { valeur: 'gauche', libelle: 'Gauche' },
            { valeur: 'droite', libelle: 'Droite' },
            { valeur: 'deux', libelle: 'Deux côtés' },
          ]}
        />
        <Champ
          libelle="Préfixe des places"
          value={prefixe}
          maxLength={20}
          onChange={(e) => setPrefixe(e.target.value)}
          required
        />
        <Champ
          libelle="Premier numéro"
          type="number"
          min={1}
          max={999999}
          value={premier}
          onChange={(e) => setPremier(Number(e.target.value))}
          required
        />
        <Champ
          libelle="Surface par place (m²)"
          type="number"
          min={0.01}
          step={0.01}
          value={surface}
          onChange={(e) => setSurface(Number(e.target.value))}
          required
        />
      </div>
      <p className={styles.aide}>
        Placement schématique à confirmer sur le terrain. Aucun loyer n’est fixé par cet outil.
      </p>
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
      <Bouton type="submit" disabled={!allee || !prefixe || action.enCours}>
        Créer la rangée
      </Bouton>
    </form>
  );
}
